import { useBrand } from '../theme/ThemeProvider'
import { TracerLogo, TracerLockupStacked } from './TracerLogo'

/*
  The lockups, as the active organization sees them.

  These wrap the Tracer marks rather than replacing them: with no theme, or with a theme
  that sets colours but supplies no logo, what renders is exactly the mark that shipped —
  recoloured, because its fills are brand tokens. A customer who supplies their own mark
  gets theirs instead.

  THE MARK IS ALWAYS AN <img>, NEVER INLINED MARKUP

  A customer's logo is a string an organization administrator typed. Inlined as markup,
  an SVG can carry <script>, an onload handler, or a <foreignObject> full of HTML — which
  would make the branding editor a stored-XSS console pointed at everyone in the
  organization. Loaded through <img>, the same SVG renders in a non-scripting context and
  can do none of it. The API refuses anything that is not an https URL or a data: image;
  this is the other half of that bargain, and neither half is redundant.
*/

/** Mark then wordmark, horizontally. The header lockup. */
export function BrandLockup({ size = 32, onDark = false }: { size?: number; onDark?: boolean }) {
  const brand = useBrand()

  return (
    <span className="inline-flex items-center" style={{ gap: size * 0.18 }}>
      <BrandMark size={size} onDark={onDark} />

      {brand.showWordmark && (
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            letterSpacing: '-0.045em',
            lineHeight: 1,
            fontSize: size * 0.6,
            whiteSpace: 'nowrap',
            color: onDark ? 'var(--color-on-primary)' : 'var(--color-primary)',
          }}
        >
          {brand.productName}
        </span>
      )}
    </span>
  )
}

/**
 * The mark alone.
 *
 * A customer's mark is given the height and allowed whatever width its aspect ratio
 * wants, up to four times the height. A square mark and a wide lockup both work; a
 * banner does not silently push the navigation off the header.
 */
export function BrandMark({ size = 32, onDark = false }: { size?: number; onDark?: boolean }) {
  const brand = useBrand()

  if (brand.markUrl) {
    return (
      <img
        src={brand.markUrl}
        alt={brand.productName}
        style={{ height: size, width: 'auto', maxWidth: size * 4, objectFit: 'contain' }}
      />
    )
  }

  return <TracerLogo size={size} variant={onDark ? 'reversed' : 'full'} />
}

/**
 * Wordmark over the streak, with an optional tagline. The sign-in and organization-gate
 * lockup.
 *
 * A customer with their own mark gets it stacked over the tagline instead: the streak is
 * Tracer's mark, and reusing it under somebody else's name would be putting two brands
 * in one lockup.
 */
export function BrandLockupStacked({
  size = 40,
  onDark = false,
  showTagline = false,
  waiting = false,
}: {
  size?: number
  onDark?: boolean
  showTagline?: boolean
  /** Animates the lockup while something is loading. See `TracerLockupStacked`. */
  waiting?: boolean
}) {
  const brand = useBrand()

  if (!brand.markUrl) {
    return (
      <TracerLockupStacked
        size={size}
        onDark={onDark}
        showTagline={showTagline}
        productName={brand.productName}
        tagline={brand.tagline}
        waiting={waiting}
      />
    )
  }

  return (
    <span className="inline-flex flex-col items-center gap-2">
      <img
        src={brand.markUrl}
        alt={brand.productName}
        style={{ height: size * 1.4, width: 'auto', maxWidth: size * 6, objectFit: 'contain' }}
      />

      {brand.showWordmark && (
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            letterSpacing: '-0.045em',
            lineHeight: 1,
            fontSize: size * 0.7,
            color: onDark ? 'var(--color-on-primary)' : 'var(--color-primary)',
          }}
        >
          {brand.productName}
        </span>
      )}

      {showTagline && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: size * 0.19,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            opacity: 0.6,
            color: onDark ? 'var(--color-on-primary)' : 'var(--color-muted)',
          }}
        >
          {brand.tagline}
        </span>
      )}

      {waiting && <WaitingDots size={size} onDark={onDark} />}
    </span>
  )
}

/**
 * The waiting sequence for an organization that supplies its own mark.
 *
 * They have no streak to animate — and they should not borrow Tracer's, for the same
 * reason `BrandLockupStacked` does not stack their logo over it: that would be two brands
 * in one lockup. So the left-to-right sequence is kept and the shape is made neutral.
 *
 * Their palette, not Tracer's: the dots are brand tokens, so this arrives already
 * recoloured by whatever theme is applied.
 */
function WaitingDots({ size, onDark }: { size: number; onDark: boolean }) {
  const dot = size * 0.13

  return (
    <span
      aria-hidden
      className="inline-flex items-center"
      style={{ gap: dot * 0.9, marginTop: size * 0.25 }}
    >
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="dot-run--waiting"
          style={{
            width: dot,
            height: dot,
            borderRadius: 9999,
            background: onDark ? 'var(--color-beam)' : 'var(--color-accent)',
            animationDelay: `${index * 0.16}s`,
          }}
        />
      ))}
    </span>
  )
}
